import clsx from "clsx";
import { CSSProperties, FC } from "react";

import { valueAsString } from "@tsmono/util";

import { ScoreValue } from "../../../@types/extraInspect";
import { kScoreTypeBoolean, kScoreTypePassFail } from "../../../constants";

import { scoreTone, Tone } from "./scoreTone";
import styles from "./ScoreValueDisplay.module.css";

interface ScoreValueDisplayProps {
  // null: dict-valued scores admit null entries (see scoreValue).
  value: ScoreValue | null | undefined;
  scoreType: string;
  /** Pixel diameter for grade / bool circles. Plain text scales with
   *  the surrounding font size and ignores this. */
  size?: number;
  /** Optional CSS color expression resolved from the eval-author's
   *  `score_color_scales`. When set on the chip variant, paints the
   *  mini-pill background instead of the tone-derived class. */
  bgColor?: string;
}

/**
 * Standalone V2 renderer for a single score value. Bypasses the
 * existing per-descriptor `render()` so we can size grade/bool circles
 * independently for the header's compact / row / chip contexts.
 *
 * - passfail (C/A/B/I/F/P/N) → solid circle, white glyph
 * - boolean → solid circle with "T" or "F"
 * - everything else → tabular-nums text, tone-colored when explicit
 *
 * Colors come from theme tokens (--bs-success / --bs-danger /
 * --bs-orange) so dark/light handle automatically.
 */
/**
 * Shared circle renderer for passfail / boolean scores. Returns null
 * for any other score type so callers can fall through to their own
 * non-circle branch (plain text vs mini-pill).
 */
const CircleValue: FC<ScoreValueDisplayProps> = ({
  value,
  scoreType,
  size = 18,
}) => {
  const tone = scoreTone(value, scoreType);
  const sizeStyle: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.55),
  };

  // != null: a null dict sub-score falls through to plain text (rendered
  // empty) instead of a circle whose glyph would read "null".
  if (scoreType === kScoreTypePassFail && value != null) {
    return (
      <span
        className={clsx(styles.circle, toneCircleClass(tone))}
        style={sizeStyle}
      >
        {valueAsString(value)}
      </span>
    );
  }

  if (scoreType === kScoreTypeBoolean) {
    return (
      <span
        className={clsx(styles.circle, toneCircleClass(tone))}
        style={sizeStyle}
        title={value ? "true" : "false"}
      >
        {value ? "T" : "F"}
      </span>
    );
  }

  return null;
};

const isCircleScoreType = (
  scoreType: string,
  value: ScoreValue | null | undefined
) =>
  (scoreType === kScoreTypePassFail && value != null) ||
  scoreType === kScoreTypeBoolean;

export const ScoreValueDisplay: FC<ScoreValueDisplayProps> = ({
  value,
  scoreType,
  size = 18,
}) => {
  if (isCircleScoreType(scoreType, value)) {
    return <CircleValue value={value} scoreType={scoreType} size={size} />;
  }
  const tone = scoreTone(value, scoreType);
  const formatted = formatPlainValue(value);
  return (
    <span className={clsx(styles.text, toneTextClass(tone))} title={formatted}>
      {formatted}
    </span>
  );
};

/** Smaller variant used inside chip pills — numbers/strings render as
 *  a colored mini-pill instead of plain text so the tone reads at
 *  small sizes. */
export const ScoreChipValueDisplay: FC<ScoreValueDisplayProps> = ({
  value,
  scoreType,
  size = 16,
  bgColor,
}) => {
  if (isCircleScoreType(scoreType, value)) {
    return <CircleValue value={value} scoreType={scoreType} size={size} />;
  }
  const tone = scoreTone(value, scoreType);
  const formatted = formatPlainValue(value);
  // A configured `bgColor` wins over the tone-derived class so the
  // pill matches the chip border. Fall through to the tone class when
  // no scale is configured for this score.
  const pillStyle: CSSProperties | undefined = bgColor
    ? { backgroundColor: bgColor }
    : undefined;
  return (
    <span
      className={clsx(styles.miniPill, !bgColor && toneMiniPillClass(tone))}
      style={pillStyle}
      title={formatted}
    >
      {formatted}
    </span>
  );
};

function toneCircleClass(tone: Tone): string | undefined {
  if (tone === "pass") return styles.circlePass;
  if (tone === "fail") return styles.circleFail;
  if (tone === "warn") return styles.circleWarn;
  return styles.circleNeutral;
}

function toneTextClass(tone: Tone): string | undefined {
  if (tone === "pass") return styles.textPass;
  if (tone === "fail") return styles.textFail;
  if (tone === "warn") return styles.textWarn;
  return styles.textNeutral;
}

function toneMiniPillClass(tone: Tone): string | undefined {
  if (tone === "pass") return styles.miniPillPass;
  if (tone === "fail") return styles.miniPillFail;
  if (tone === "warn") return styles.miniPillWarn;
  return styles.miniPillNeutral;
}

function formatPlainValue(v: ScoreValue | null | undefined): string {
  if (v === undefined || v === null) return "";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
